/**
 * Тосты.
 *
 * Тост сообщает о результате действия, которого пользователь не видит на экране
 * (docs/04, §9.7). Ошибка, требующая решения, показывается не тостом, а
 * `InlineAlert` рядом с местом ошибки — тост исчезнет раньше, чем его прочтут.
 */

import * as React from "react";
import * as RadixToast from "@radix-ui/react-toast";
import { CheckCircle2, Info, X, XCircle } from "lucide-react";

import { randomId } from "@/shared/lib/uuid";
import { cn } from "@/shared/lib/cn";

export type ToastTone = "info" | "success" | "danger";

export interface ToastMessage {
  id: string;
  title: string;
  description?: string;
  tone?: ToastTone;
  /** Действие вроде «Отменить»: даёт вернуть только что сделанное. */
  action?: { label: string; onClick: () => void };
  durationMs?: number;
}

interface ToastContextValue {
  show: (message: Omit<ToastMessage, "id">) => void;
}

const ToastContext = React.createContext<ToastContextValue | null>(null);

const TONE_ICON = {
  info: Info,
  success: CheckCircle2,
  danger: XCircle,
} as const;

const TONE_CLASS: Record<ToastTone, string> = {
  info: "text-[var(--pt-info-text)]",
  success: "text-[var(--pt-success-text)]",
  danger: "text-[var(--pt-danger-text)]",
};

export function useToast(): ToastContextValue {
  const context = React.useContext(ToastContext);
  if (context === null) {
    throw new Error("useToast используется вне ToastProvider");
  }
  return context;
}

export function ToastProvider({ children }: { children: React.ReactNode }): React.ReactElement {
  const [messages, setMessages] = React.useState<ToastMessage[]>([]);

  const show = React.useCallback((message: Omit<ToastMessage, "id">) => {
    setMessages((current) => [...current, { ...message, id: randomId() }]);
  }, []);

  const value = React.useMemo(() => ({ show }), [show]);

  return (
    <ToastContext.Provider value={value}>
      <RadixToast.Provider swipeDirection="right" duration={5000}>
        {children}

        {messages.map((message) => {
          const tone = message.tone ?? "info";
          const Icon = TONE_ICON[tone];
          return (
            <RadixToast.Root
              key={message.id}
              duration={message.durationMs}
              onOpenChange={(open) => {
                if (!open) {
                  setMessages((current) => current.filter((item) => item.id !== message.id));
                }
              }}
              className={cn(
                "flex items-start gap-[var(--pt-space-8)] rounded-[var(--pt-radius-5)]",
                "border border-[var(--pt-border-default)] bg-[var(--pt-bg-raised)]",
                "p-[var(--pt-space-12)] shadow-[var(--pt-shadow-3)]",
                "data-[state=open]:animate-in data-[state=closed]:animate-out",
              )}
            >
              <Icon
                aria-hidden
                className={cn("mt-[1px] size-[var(--pt-icon)] shrink-0", TONE_CLASS[tone])}
              />
              <div className="min-w-0 flex-1">
                <RadixToast.Title className="text-[var(--pt-font-ui)] font-medium text-[var(--pt-text-primary)]">
                  {message.title}
                </RadixToast.Title>
                {message.description && (
                  <RadixToast.Description className="mt-[var(--pt-space-2)] text-[var(--pt-fs-xs)] leading-[var(--pt-lh-xs)] text-[var(--pt-text-secondary)]">
                    {message.description}
                  </RadixToast.Description>
                )}
              </div>
              {message.action && (
                <RadixToast.Action asChild altText={message.action.label}>
                  <button
                    type="button"
                    onClick={message.action.onClick}
                    className="shrink-0 text-[var(--pt-font-ui)] text-[var(--pt-accent-text)] hover:underline"
                  >
                    {message.action.label}
                  </button>
                </RadixToast.Action>
              )}
              <RadixToast.Close
                aria-label="Закрыть уведомление"
                className="shrink-0 text-[var(--pt-text-tertiary)] hover:text-[var(--pt-text-primary)]"
              >
                <X className="size-[14px]" />
              </RadixToast.Close>
            </RadixToast.Root>
          );
        })}

        <RadixToast.Viewport
          className={cn(
            "fixed bottom-[var(--pt-space-16)] right-[var(--pt-space-16)] z-[var(--pt-z-toast)]",
            "flex w-[360px] max-w-[calc(100vw-32px)] flex-col gap-[var(--pt-space-8)] outline-none",
          )}
        />
      </RadixToast.Provider>
    </ToastContext.Provider>
  );
}
